import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const CryptographicKeyManagement = ({ keySettings, onKeySettingsChange }) => {
  const [showKeyDetails, setShowKeyDetails] = useState({});
  const [showRotationModal, setShowRotationModal] = useState(false);
  const [selectedKeyForRotation, setSelectedKeyForRotation] = useState(null);

  const mockKeys = [
    {
      id: 'signing-key-001',
      type: 'Signing Key',
      algorithm: 'Ed25519',
      purpose: 'Credential Signing',
      created: '2024-01-15T10:30:00Z',
      lastUsed: '2024-09-02T14:22:00Z',
      status: 'active',
      strength: 'high',
      usageCount: 1247,
      expiryDate: '2025-01-15T10:30:00Z'
    },
    {
      id: 'encryption-key-001',
      type: 'Encryption Key',
      algorithm: 'X25519',
      purpose: 'Data Encryption',
      created: '2024-01-15T10:35:00Z',
      lastUsed: '2024-09-02T13:45:00Z',
      status: 'active',
      strength: 'high',
      usageCount: 892,
      expiryDate: '2025-01-15T10:35:00Z'
    },
    {
      id: 'verification-key-001',
      type: 'Verification Key',
      algorithm: 'secp256k1',
      purpose: 'Identity Verification',
      created: '2024-02-20T09:15:00Z',
      lastUsed: '2024-09-01T16:30:00Z',
      status: 'active',
      strength: 'high',
      usageCount: 634,
      expiryDate: '2025-02-20T09:15:00Z'
    },
    {
      id: 'backup-key-001',
      type: 'Backup Key',
      algorithm: 'RSA-4096',
      purpose: 'Key Recovery',
      created: '2024-01-15T11:00:00Z',
      lastUsed: 'Never',
      status: 'standby',
      strength: 'maximum',
      usageCount: 0,
      expiryDate: '2026-01-15T11:00:00Z'
    }
  ];

  const rotationScheduleOptions = [
    { value: 'never', label: 'Manual Only' },
    { value: '30', label: 'Every 30 days' },
    { value: '90', label: 'Every 90 days' },
    { value: '180', label: 'Every 6 months' },
    { value: '365', label: 'Annually' }
  ];

  const backupFrequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'manual', label: 'Manual Only' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'standby': return 'text-warning';
      case 'expired': return 'text-error';
      case 'compromised': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'standby': return 'Clock';
      case 'expired': return 'XCircle';
      case 'compromised': return 'AlertTriangle';
      default: return 'Circle';
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'maximum': return 'text-success';
      case 'high': return 'text-success';
      case 'medium': return 'text-warning';
      case 'low': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStrengthIcon = (strength) => {
    switch (strength) {
      case 'maximum': return 'ShieldCheck';
      case 'high': return 'Shield';
      case 'medium': return 'ShieldAlert';
      case 'low': return 'ShieldX';
      default: return 'Shield';
    }
  };

  const toggleKeyDetails = (keyId) => {
    setShowKeyDetails(prev => ({
      ...prev,
      [keyId]: !prev?.[keyId]
    }));
  };

  const handleRotationScheduleChange = (keyType, schedule) => {
    const updatedSettings = {
      ...keySettings,
      cryptographicKeys: {
        ...keySettings?.cryptographicKeys,
        rotationSchedule: {
          ...keySettings?.cryptographicKeys?.rotationSchedule,
          [keyType]: schedule
        }
      }
    };
    onKeySettingsChange(updatedSettings);
  };

  const handleBackupSettingChange = (setting, value) => {
    const updatedSettings = {
      ...keySettings,
      cryptographicKeys: {
        ...keySettings?.cryptographicKeys,
        backup: {
          ...keySettings?.cryptographicKeys?.backup,
          [setting]: value
        }
      }
    };
    onKeySettingsChange(updatedSettings);
  };

  const initiateKeyRotation = (keyId) => {
    setSelectedKeyForRotation(keyId);
    setShowRotationModal(true);
  };

  const confirmKeyRotation = () => {
    // Handle key rotation logic here
    setShowRotationModal(false);
    setSelectedKeyForRotation(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Cryptographic Key Management</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure key rotation schedules, backup preferences, and security settings
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Key" size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">
            {mockKeys?.filter(k => k?.status === 'active')?.length} Active Keys
          </span>
        </div>
      </div>
      {/* Key Inventory */}
      <div className="space-y-4 mb-6">
        <h4 className="font-medium text-foreground">Key Inventory</h4>
        {mockKeys?.map((key) => {
          const isExpanded = showKeyDetails?.[key?.id];
          const daysUntilExpiry = getDaysUntilExpiry(key?.expiryDate);
          
          return (
            <div key={key?.id} className="border border-border rounded-lg">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleKeyDetails(key?.id)}
                      className="w-8 h-8"
                    >
                      <Icon 
                        name={isExpanded ? "ChevronDown" : "ChevronRight"} 
                        size={16} 
                      />
                    </Button>
                    <div>
                      <h5 className="font-medium text-foreground">{key?.type}</h5>
                      <p className="text-xs text-muted-foreground">{key?.purpose}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Icon 
                        name={getStatusIcon(key?.status)} 
                        size={16} 
                        className={getStatusColor(key?.status)} 
                      />
                      <span className={`text-xs font-medium ${getStatusColor(key?.status)}`}>
                        {key?.status}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Icon 
                        name={getStrengthIcon(key?.strength)} 
                        size={16} 
                        className={getStrengthColor(key?.strength)} 
                      />
                      <span className={`text-xs font-medium ${getStrengthColor(key?.strength)}`}>
                        {key?.strength}
                      </span>
                    </div>
                    
                    {key?.status === 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RotateCcw"
                        iconPosition="left"
                        onClick={() => initiateKeyRotation(key?.id)}
                      >
                        Rotate
                      </Button>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-4 pl-11 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div>
                        <span className="text-muted-foreground">Algorithm:</span>
                        <span className="ml-2 text-foreground font-mono">{key?.algorithm}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Created:</span>
                        <span className="ml-2 text-foreground">{formatDate(key?.created)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Last Used:</span>
                        <span className="ml-2 text-foreground">
                          {key?.lastUsed === 'Never' ? 'Never' : formatDate(key?.lastUsed)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <span className="text-muted-foreground">Usage Count:</span>
                        <span className="ml-2 text-foreground">{key?.usageCount?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Expires:</span>
                        <span className="ml-2 text-foreground">
                          {formatDate(key?.expiryDate)}
                          {daysUntilExpiry > 0 && daysUntilExpiry <= 90 && (
                            <span className="ml-1 text-warning">({daysUntilExpiry} days)</span>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Key ID:</span>
                        <span className="ml-2 text-foreground font-mono text-xs">{key?.id}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Rotation Settings */}
      <div className="space-y-4 mb-6">
        <h4 className="font-medium text-foreground">Automatic Key Rotation</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Signing Keys</label>
            <Select
              options={rotationScheduleOptions}
              value={keySettings?.cryptographicKeys?.rotationSchedule?.signing || 'never'}
              onChange={(value) => handleRotationScheduleChange('signing', value)}
              placeholder="Select rotation schedule"
            />
          </div>
          
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Encryption Keys</label>
            <Select
              options={rotationScheduleOptions}
              value={keySettings?.cryptographicKeys?.rotationSchedule?.encryption || 'never'}
              onChange={(value) => handleRotationScheduleChange('encryption', value)}
              placeholder="Select rotation schedule"
            />
          </div>
        </div>
      </div>
      {/* Backup Settings */}
      <div className="space-y-4 mb-6">
        <h4 className="font-medium text-foreground">Key Backup Settings</h4>
        <div className="space-y-4">
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Backup Frequency</label>
            <Select
              options={backupFrequencyOptions}
              value={keySettings?.cryptographicKeys?.backup?.frequency || 'weekly'}
              onChange={(value) => handleBackupSettingChange('frequency', value)}
              placeholder="Select backup frequency"
            />
          </div>
          
          <div className="space-y-3">
            <Checkbox
              label="Enable encrypted backups"
              description="Encrypt key backups with additional passphrase protection"
              checked={keySettings?.cryptographicKeys?.backup?.encrypted || false}
              onChange={(e) => handleBackupSettingChange('encrypted', e?.target?.checked)}
            />
            
            <Checkbox
              label="Multi-location backup"
              description="Store backups in multiple secure locations for redundancy"
              checked={keySettings?.cryptographicKeys?.backup?.multiLocation || false}
              onChange={(e) => handleBackupSettingChange('multiLocation', e?.target?.checked)}
            />
            
            <Checkbox
              label="Automatic backup verification"
              description="Regularly verify the integrity of stored key backups"
              checked={keySettings?.cryptographicKeys?.backup?.autoVerify || false}
              onChange={(e) => handleBackupSettingChange('autoVerify', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Key Rotation Modal */}
      {showRotationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="RotateCcw" size={24} className="text-primary" />
              <h3 className="text-lg font-semibold text-foreground">Rotate Cryptographic Key</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              This will generate a new cryptographic key and securely retire the current one. 
              All future operations will use the new key. This process cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRotationModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={confirmKeyRotation}
                className="flex-1"
              >
                Rotate Key
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium">Key Security</p>
            <p className="text-muted-foreground mt-1">
              All cryptographic keys are stored using hardware security modules (HSM) or secure enclaves. 
              Regular rotation and secure backup practices ensure maximum protection of your digital identity.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptographicKeyManagement;